import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBell, FiX, FiTrendingDown, FiTrendingUp, FiDollarSign, FiMail, FiCheck } = FiIcons;

function PriceAlerts() {
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      make: 'Audi',
      model: 'Q5 Quattro',
      year: 2023,
      currentPrice: 53999,
      targetPrice: 52000,
      priceChange: -1000,
      lastUpdate: new Date('2024-01-20'),
      active: true,
      email: 'john@example.com'
    },
    {
      id: 2,
      make: 'BMW',
      model: 'X3 xDrive',
      year: 2022,
      currentPrice: 49999,
      targetPrice: 48000,
      priceChange: 500,
      lastUpdate: new Date('2024-01-18'),
      active: true,
      email: 'john@example.com'
    }
  ]);
  const [newAlert, setNewAlert] = useState({
    make: '',
    model: '',
    year: '',
    targetPrice: '',
    email: ''
  });
  const [showNewAlertForm, setShowNewAlertForm] = useState(false);

  const toggleAlert = (alertId) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, active: !alert.active } : alert
    ));
  };

  const deleteAlert = (alertId) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const createAlert = () => {
    if (newAlert.make && newAlert.model && newAlert.targetPrice && newAlert.email) {
      const alert = {
        id: Date.now(),
        ...newAlert,
        year: parseInt(newAlert.year) || 2023,
        targetPrice: parseFloat(newAlert.targetPrice),
        currentPrice: parseFloat(newAlert.targetPrice) + 2000, // Mock current price
        priceChange: 0,
        lastUpdate: new Date(),
        active: true
      };
      setAlerts(prev => [...prev, alert]);
      setNewAlert({ make: '', model: '', year: '', targetPrice: '', email: '' });
      setShowNewAlertForm(false);
    }
  };

  return (
    <>
      {/* Price Alerts Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed top-40 right-6 bg-orange-500 text-white p-3 rounded-full shadow-lg z-40 hover:bg-orange-600 transition-colors"
      >
        <SafeIcon icon={FiBell} className="w-5 h-5" />
        {alerts.filter(alert => alert.active).length > 0 && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
            <span className="text-xs font-bold">{alerts.filter(alert => alert.active).length}</span>
          </div>
        )}
      </motion.button>

      {/* Price Alerts Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Halifax Price Alerts</h2>
                    <p className="text-orange-100">
                      {alerts.filter(alert => alert.active).length} active alert{alerts.filter(alert => alert.active).length !== 1 ? 's' : ''} • 
                      Get notified when prices drop in Halifax
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowNewAlertForm(true)}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors flex items-center space-x-2"
                    >
                      <SafeIcon icon={FiBell} className="w-4 h-4" />
                      <span>New Alert</span>
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-96 overflow-y-auto">
                {/* New Alert Form */}
                {showNewAlertForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-blue-50 rounded-lg p-6 mb-6 border border-blue-200"
                  >
                    <h3 className="text-lg font-semibold text-blue-900 mb-4">Create Halifax Price Alert</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        placeholder="Make (e.g., Audi)"
                        value={newAlert.make}
                        onChange={(e) => setNewAlert({...newAlert, make: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Model (e.g., Q5)"
                        value={newAlert.model}
                        onChange={(e) => setNewAlert({...newAlert, model: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="number"
                        placeholder="Year (e.g., 2023)"
                        value={newAlert.year}
                        onChange={(e) => setNewAlert({...newAlert, year: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="number"
                        placeholder="Target Price (CAD)"
                        value={newAlert.targetPrice}
                        onChange={(e) => setNewAlert({...newAlert, targetPrice: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <input
                      type="email"
                      placeholder="Your email address"
                      value={newAlert.email}
                      onChange={(e) => setNewAlert({...newAlert, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
                    />
                    <div className="flex space-x-3">
                      <button
                        onClick={createAlert}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Create Alert
                      </button>
                      <button
                        onClick={() => setShowNewAlertForm(false)}
                        className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Alerts List */}
                {alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <SafeIcon icon={FiBell} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No price alerts yet</h3>
                    <p className="text-gray-600 mb-6">
                      Set up alerts to get notified when Halifax vehicle prices drop to your target.
                    </p>
                    <button
                      onClick={() => setShowNewAlertForm(true)}
                      className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      Create Your First Alert
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {alerts.map((alert, index) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`border-2 rounded-lg p-4 ${
                          alert.active ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded-full ${
                              alert.active ? 'bg-orange-500' : 'bg-gray-400'
                            }`}></div>
                            <h3 className="font-semibold text-gray-900">
                              {alert.year} {alert.make} {alert.model}
                            </h3>
                            {alert.priceChange !== 0 && (
                              <div className={`flex items-center space-x-1 ${
                                alert.priceChange > 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                <SafeIcon 
                                  icon={alert.priceChange > 0 ? FiTrendingUp : FiTrendingDown} 
                                  className="w-4 h-4" 
                                />
                                <span className="text-sm font-medium">
                                  {alert.priceChange > 0 ? '+' : ''}${Math.abs(alert.priceChange).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => toggleAlert(alert.id)}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                alert.active 
                                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {alert.active ? 'Active' : 'Paused'}
                            </button>
                            <button
                              onClick={() => deleteAlert(alert.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                              <SafeIcon icon={FiX} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Current Halifax Price</p>
                            <p className="text-lg font-bold text-gray-900">
                              ${alert.currentPrice.toLocaleString()} CAD
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Your Target Price</p>
                            <p className="text-lg font-bold text-orange-600">
                              ${alert.targetPrice.toLocaleString()} CAD
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Difference</p>
                            <p className={`text-lg font-bold ${
                              alert.currentPrice <= alert.targetPrice ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {alert.currentPrice <= alert.targetPrice ? (
                                <span className="flex items-center space-x-1">
                                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                                  <span>Target Met!</span>
                                </span>
                              ) : (
                                `$${(alert.currentPrice - alert.targetPrice).toLocaleString()}`
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiMail} className="w-3 h-3" />
                              <span>{alert.email}</span>
                            </div>
                            <span>Updated {alert.lastUpdate.toLocaleDateString()}</span>
                          </div>
                          {alert.currentPrice <= alert.targetPrice && (
                            <button className="px-3 py-1 bg-green-600 text-white rounded-full text-xs hover:bg-green-700 transition-colors">
                              View Deal
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <p>Halifax price alerts update daily • Email notifications sent instantly</p>
                    <p className="text-xs mt-1">Average Halifax price drop: $1,200 CAD over 30 days</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">
                      Save an average of $2,100 CAD with Halifax price alerts
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default PriceAlerts;